import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { createLocalId } from "../utils/id";

const cartContext = createContext(null);

const CART_STORAGE_KEY = "gold_cart_items_v1";

function normalizeCartItem(item) {
  if (!item || typeof item !== "object") return item;
  const rawQty = Number.parseInt(String(item.cantidad ?? item.fracciones ?? 1), 10) || 1;
  const parsedMax = Number.parseInt(
    String(item.maxFracciones ?? item.number_fractions ?? ""),
    10
  );
  // Sin máximo guardado (ítems viejos): no recortar; con máximo: respetarlo
  const max =
    Number.isFinite(parsedMax) && parsedMax > 0
      ? parsedMax
      : Math.max(rawQty, 1);
  const cantidad = Math.min(Math.max(1, rawQty), max);
  const unit = Number(item.precio ?? item.precioFraccion ?? 0);
  const value = unit * cantidad;
  return {
    ...item,
    maxFracciones: max,
    cantidad,
    fracciones: String(cantidad),
    value,
    total: value,
  };
}

function loadCartFromStorage() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeCartItem);
  } catch {
    return [];
  }
}

function saveCartToStorage(items) {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.warn("No se pudo persistir el carrito:", e);
  }
}

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => loadCartFromStorage());
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    saveCartToStorage(items);
  }, [items]);

  const addToCart = useCallback((item) => {
    setItems((prev) => [...prev, normalizeCartItem({ ...item, id: item.id || createLocalId() })]);
    setIsOpen(true);
  }, []);

  const getFraccionesEnCarrito = useCallback(
    (lotteryId, numero, series) => {
      return items
        .filter(
          (item) =>
            item.lotteryId === lotteryId && item.numero === numero && item.series === series
        )
        .reduce((acc, item) => acc + (item.cantidad || 1), 0);
    },
    [items]
  );

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const updateQuantity = useCallback((id, newQuantity) => {
    if (newQuantity < 1) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const parsedMax = Number.parseInt(
          String(item.maxFracciones ?? item.number_fractions ?? ""),
          10
        );
        const ticketMax =
          Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : newQuantity;
        const availRaw = Number.parseInt(String(item.availableFractions ?? ""), 10);
        const hasAvail = Number.isFinite(availRaw) && availRaw >= 0;
        const max = hasAvail
          ? Math.max(0, Math.min(ticketMax, availRaw))
          : ticketMax;
        if (max <= 0) {
          return {
            ...item,
            maxFracciones: ticketMax,
            isSoldOut: true,
          };
        }
        const cantidad = Math.min(Math.max(1, newQuantity), max);
        const unit = Number(item.precio ?? item.precioFraccion ?? 0);
        const value = unit * cantidad;
        return {
          ...item,
          cantidad,
          fracciones: String(cantidad),
          value,
          total: value,
          maxFracciones: ticketMax,
          isSoldOut: false,
        };
      })
    );
  }, []);

  const setItemAvailability = useCallback((id, { availableFractions, isSoldOut }) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const parsedMax = Number.parseInt(
          String(item.maxFracciones ?? item.number_fractions ?? ""),
          10
        );
        const ticketMax =
          Number.isFinite(parsedMax) && parsedMax > 0
            ? parsedMax
            : Math.max(item.cantidad || 1, 1);
        const avail = Number.isFinite(Number(availableFractions))
          ? Math.max(0, Number(availableFractions))
          : ticketMax;
        const soldOut = Boolean(isSoldOut) || avail <= 0;
        const effectiveMax = soldOut ? 0 : Math.min(ticketMax, avail);
        const rawQty =
          Number.parseInt(String(item.cantidad ?? item.fracciones ?? 1), 10) || 1;
        const cantidad =
          effectiveMax <= 0 ? Math.max(1, rawQty) : Math.min(rawQty, effectiveMax);
        const unit = Number(item.precio ?? item.precioFraccion ?? 0);
        const value = unit * cantidad;
        return {
          ...item,
          maxFracciones: ticketMax,
          availableFractions: avail,
          isSoldOut: soldOut,
          cantidad,
          fracciones: String(cantidad),
          value,
          total: value,
        };
      })
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setIsOpen(false);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const getTotal = useCallback(() => {
    return items.reduce((acc, item) => acc + (item.precio || 0) * (item.cantidad || 1), 0);
  }, [items]);

  const getItemCount = useCallback(() => {
    return items.reduce((acc, item) => acc + (item.cantidad || 1), 0);
  }, [items]);

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  return (
    <cartContext.Provider
      value={{
        items,
        isOpen,
        openCart,
        closeCart,
        addToCart,
        removeItem,
        updateQuantity,
        setItemAvailability,
        clearCart,
        getTotal,
        getItemCount,
        getFraccionesEnCarrito,
      }}
    >
      {children}
    </cartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(cartContext);
  if (!context) {
    throw new Error("useCart debe usarse dentro de <CartProvider>");
  }
  return context;
};
